/**
 * Зургийн самбар — байршуулах, харах, устгах.
 *
 * ⚠️ Файл АПП-ААР ДАМЖИХГҮЙ. presign → хөтөч хадгалалт руу ШУУД PUT →
 *    confirm. Тиймээс upload-ын `fetch` нь `api()` туслахыг
 *    ашиглахгүй: тэр нь Authorization header болон cookie нэмдэг
 *    бөгөөд presigned URL-ийн гарын үсгийг эвдэнэ.
 */

import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PHOTO_ROLE_LABEL, type Photo, type PhotoPresign } from '../lib/types';
import { Button, Empty, ErrorNote, Spinner } from './ui';

/** Сервертэй ижил жагсаалт — `PhotoContentType`. */
const ACCEPT = 'image/jpeg,image/png,image/webp';
const MAX_BYTES = 25 * 1024 * 1024;

interface Props {
  /** `items` эсвэл `warranty/claims` — API-ийн зам. */
  base: string;
  ownerId: string;
  title?: string;
}

export function PhotoPanel({ base, ownerId, title = 'Зураг' }: Props) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState<Photo | null>(null);

  const canEdit = can('WAREHOUSE', 'ADMIN', 'COLLECTOR', 'STORE_MANAGER', 'SALESPERSON');
  const canDelete = can('WAREHOUSE', 'ADMIN');
  const path = `/${base}/${ownerId}/photos`;

  const photos = useQuery({
    queryKey: ['photos', base, ownerId],
    queryFn: () => api<Envelope<Photo[]>>(path),
    // Боловсруулалт дуустал богино давтамжтай асууна
    refetchInterval: (q) =>
      q.state.data?.data.some((p) => p.status === 'PENDING' || p.status === 'PROCESSING')
        ? 2000
        : false,
  });

  async function upload(file: File) {
    setError(null);
    if (file.size > MAX_BYTES) {
      setError('Зураг 25 MB-аас том байна');
      return;
    }
    setBusy(true);
    try {
      const { data: pre } = await api<Envelope<PhotoPresign>>(`${path}/presign`, {
        method: 'POST',
        body: { contentType: file.type, bytes: file.size },
      });

      // ⚠️ Энэ хүсэлт хадгалалт руу ШУУД явна — `api()` ашиглаж БОЛОХГҮЙ
      const put = await fetch(pre.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!put.ok) throw new Error(`Байршуулж чадсангүй (${put.status})`);

      await api(`${path}/confirm`, {
        method: 'POST',
        body: { photoId: pre.photoId, contentType: file.type, role: 'DETAIL' },
      });

      await qc.invalidateQueries({ queryKey: ['photos', base, ownerId] });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Байршуулахад алдаа гарлаа');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const setPrimary = useMutation({
    mutationFn: (id: string) => api(`/photos/${id}`, { method: 'PATCH', body: { role: 'PRIMARY' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['photos', base, ownerId] }),
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/photos/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['photos', base, ownerId] }),
    onError: (e: Error) => setError(e.message),
  });

  const rows = photos.data?.data ?? [];

  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="num text-[12.5px] text-ink-2">{rows.length}</span>
        {canEdit ? (
          <span className="ml-auto">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void upload(f);
              }}
            />
            <Button variant="ghost" disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? 'Байршуулж байна…' : 'Зураг нэмэх'}
            </Button>
          </span>
        ) : null}
      </div>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      {photos.isPending ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>Зураг байхгүй</Empty>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {rows.map((p) => (
            <li key={p.id} className="flex flex-col gap-1.5">
              <button
                type="button"
                onClick={() => setZoom(p)}
                className="relative aspect-4/3 overflow-hidden rounded-card border border-hair bg-well"
              >
                {p.mediumUrl ?? p.thumbUrl ? (
                  <img
                    /* Medium (800px). Thumb нь энэ хэмжээний нүдэнд,
                       ялангуяа 2x дэлгэц дээр бүдэг харагдана — гутал
                       ТАНИГДАХ ёстой. Thumb нь зөвхөн medium бэлэн
                       болоогүй үеийн нөөц зам. */
                    src={p.mediumUrl ?? p.thumbUrl ?? ''}
                    alt={PHOTO_ROLE_LABEL[p.role]}
                    loading="lazy"
                    className="size-full object-cover"
                  />
                ) : (
                  <span className="grid size-full place-items-center px-2 text-center text-[11.5px] text-ink-2">
                    {p.status === 'FAILED' ? 'Боловсруулж чадсангүй' : 'Боловсруулж байна…'}
                  </span>
                )}
                {p.role === 'PRIMARY' ? (
                  <span className="absolute top-1.5 left-1.5 rounded-full bg-accent px-2 py-[2px] text-[10.5px] text-white">
                    Үндсэн
                  </span>
                ) : null}
              </button>

              <div className="flex items-center gap-2 text-[11.5px] text-ink-2">
                <span>{PHOTO_ROLE_LABEL[p.role]}</span>
                {canEdit && p.role !== 'PRIMARY' ? (
                  <button
                    type="button"
                    onClick={() => setPrimary.mutate(p.id)}
                    className="text-accent-700 hover:underline"
                  >
                    Үндсэн болгох
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    onClick={() => remove.mutate(p.id)}
                    className="ml-auto text-alert-ink hover:underline"
                  >
                    Устгах
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Томруулж харах — баталгааны маргаанд эх хувилбар хэрэгтэй */}
      {zoom ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setZoom(null)}
        >
          {/*
            ⚠️ Хэмжээг ХАРАГДАХ ЦОНХНООС (vh/vw) авна, эцэг элементээс
               БИШ. Өмнө нь `grid place-items-center` дотор `max-h-full`
               байв: grid мөрийн өндөр агуулгаараа тодорхойлогддог тул
               хувь нь `none` болж, өндөр зураг цонхноос давж, дээд
               хэсгээрээ таслагдан харагддаг байсан.
          */}
          <img
            src={zoom.originalUrl ?? zoom.mediumUrl ?? ''}
            alt={PHOTO_ROLE_LABEL[zoom.role]}
            className="max-h-[92vh] max-w-[92vw] rounded-card object-contain"
          />
        </div>
      ) : null}
    </section>
  );
}
